// Calories vs seuil : adhérence au régime et réconciliation énergétique.

import { aggregateBy, fillDailyGaps } from './aggregate';
import { consumptionNutrients } from './macros';
import { mean, round, stddev, sum } from './statistics';
import { KCAL_PER_KG_FAT } from './threshold';
import type { Consumption, Granularity, TimeBucket, TrainingPhase } from './types';

// Demi-largeur de la bande de tolérance en récupération, en fraction du seuil
// (= maintenance). CHOIX UX ASSUMÉ, non sourcé : aucune source physiologique ne
// fixe une largeur ; ±10 % s'adapte au gabarit. Documenté dans la page Annexes.
export const RECOVERY_BAND_PCT = 0.1;

// Statut d'un jour vis-à-vis de l'objectif de la phase :
// - 'good'  : conforme à l'objectif (sous le seuil en sèche, au-dessus en masse, dans la bande en récup) ;
// - 'over'  : au-dessus (mauvais en sèche, hors cible en récup) ;
// - 'under' : en dessous (mauvais en masse) ;
// - 'off'   : hors bande en récupération (ni bien ni mal — juste hors cible).
export type DayConformity = 'good' | 'over' | 'under' | 'off';

/** Conformité d'un apport journalier à l'objectif de la phase (cœur de la sémantique couleur/métrique). */
export function conformityForDay(
  kcal: number,
  threshold: number,
  phase: TrainingPhase = 'cut',
): DayConformity {
  if (phase === 'bulk') return kcal >= threshold ? 'good' : 'under';
  if (phase === 'recovery') {
    const band = threshold * RECOVERY_BAND_PCT;
    if (kcal < threshold - band) return 'off';
    if (kcal > threshold + band) return 'off';
    return 'good';
  }
  // Sèche (défaut) : sous le seuil = conforme.
  return kcal <= threshold ? 'good' : 'over';
}

export interface DailyCalories {
  date: string;
  kcal: number;
  vsThreshold: number; // négatif = déficit, positif = dépassement
  /** Conforme à l'objectif de la phase (jour saisi uniquement). */
  onTarget: boolean;
  /** Statut détaillé (pour la couleur : good/over/under/off). */
  conformity: DayConformity;
}

// Série journalière des calories, écart au seuil. Jours non saisis comblés à 0 pour un axe continu.
export function dailyCalories(
  consumptions: readonly Consumption[],
  threshold: number,
  phase: TrainingPhase = 'cut',
): DailyCalories[] {
  const buckets = aggregateBy(
    consumptions,
    (c) => c.datetime,
    'day',
    (group) => sum(group.map((c) => consumptionNutrients(c).kcal)),
  );
  const loggedKeys = new Set(buckets.map((b) => b.start));
  return fillDailyGaps(buckets, 0).map((b) => {
    const logged = loggedKeys.has(b.start);
    const conformity = conformityForDay(b.value, threshold, phase);
    return {
      date: b.start,
      kcal: round(b.value),
      vsThreshold: round(b.value - threshold),
      // Un jour comblé (non saisi) n'est jamais « conforme » : seul un jour saisi compte.
      onTarget: logged && conformity === 'good',
      conformity,
    };
  });
}

// Calories agrégées à la granularité demandée.
export function caloriesByGranularity(
  consumptions: readonly Consumption[],
  granularity: Granularity,
): TimeBucket<number>[] {
  return aggregateBy(
    consumptions,
    (c) => c.datetime,
    granularity,
    (group) => round(sum(group.map((c) => consumptionNutrients(c).kcal))),
  );
}

export interface AdherenceStats {
  daysLogged: number;
  daysOnTarget: number; // jours conformes à l'objectif de la phase
  adherencePct: number; // % de respect parmi les jours saisis
  bestStreak: number;
  currentStreak: number;
  avgKcal: number;
  stddevKcal: number;
}

// Adhérence à l'objectif de la phase. Moyennes/taux sur les jours saisis ; streaks sur le
// calendrier continu (un jour non saisi rompt la série : deux jours conformes non contigus
// ne forment pas une streak). « Conforme » dépend de la phase (cf. conformityForDay).
export function adherence(
  consumptions: readonly Consumption[],
  threshold: number,
  phase: TrainingPhase = 'cut',
): AdherenceStats {
  const logged = aggregateBy(
    consumptions,
    (c) => c.datetime,
    'day',
    (group) => sum(group.map((c) => consumptionNutrients(c).kcal)),
  );
  const kcals = logged.map((b) => b.value);
  const onTarget = (kcal: number): boolean => conformityForDay(kcal, threshold, phase) === 'good';

  // Statut par jour : true (saisi & conforme), false (saisi & non conforme), null (non saisi → rompt).
  const loggedKeys = new Set(logged.map((b) => b.start));
  const calendar = fillDailyGaps(logged, null as number | null);
  const dayStatus = calendar.map((b) =>
    loggedKeys.has(b.start) ? onTarget(b.value as number) : null,
  );

  let best = 0;
  let running = 0;
  for (const ok of dayStatus) {
    running = ok ? running + 1 : 0;
    if (running > best) best = running;
  }
  // Streak courant : comptée depuis la fin.
  let current = 0;
  for (let i = dayStatus.length - 1; i >= 0 && dayStatus[i]; i--) current++;

  const daysOnTarget = logged.filter((b) => onTarget(b.value)).length;
  return {
    daysLogged: logged.length,
    daysOnTarget,
    adherencePct: logged.length ? round((daysOnTarget / logged.length) * 100, 1) : 0,
    bestStreak: best,
    currentStreak: current,
    avgKcal: logged.length ? round(mean(kcals)) : 0,
    stddevKcal: logged.length ? round(stddev(kcals, true)) : 0,
  };
}

export interface DailyBudget {
  threshold: number;
  consumed: number;
  remaining: number; // seuil − consommé, négatif = dépassement
  usedPct: number; // peut dépasser 100
  // 'ok' : dans la cible / il reste à manger ; 'tight' : proche du seuil (sèche) ;
  // 'over' : au-dessus (mauvais en sèche/récup) ; 'reached' : surplus atteint (bien en masse).
  status: 'ok' | 'tight' | 'over' | 'reached';
}

// Budget restant du jour : feedback prospectif avant de manger. Le statut dépend de la phase :
// en sèche on surveille le dépassement, en masse on VISE le seuil (surplus), en récup on reste
// dans la bande de tolérance autour de la maintenance.
export function dailyBudget(
  consumedToday: number,
  threshold: number,
  phase: TrainingPhase = 'cut',
): DailyBudget {
  const remaining = threshold - consumedToday;
  const usedPct = threshold > 0 ? round((consumedToday / threshold) * 100) : 0;
  let status: DailyBudget['status'];
  if (phase === 'bulk') {
    // Objectif = atteindre le surplus. Au seuil ou au-dessus = atteint ; sinon il reste à manger.
    status = consumedToday >= threshold ? 'reached' : 'ok';
  } else if (phase === 'recovery') {
    // Hors bande haute = dépassement ; sinon dans la cible.
    status = consumedToday > threshold + threshold * RECOVERY_BAND_PCT ? 'over' : 'ok';
  } else {
    status = remaining < 0 ? 'over' : usedPct >= 85 ? 'tight' : 'ok';
  }
  return {
    threshold: round(threshold),
    consumed: round(consumedToday),
    remaining: round(remaining),
    usedPct,
    status,
  };
}

export interface EnergyBalance {
  cumulativeDeficitKcal: number; // positif = déficit
  predictedLossKg: number;
}

// Déficit cumulé (TDEE − ingérées) → perte théorique (Wishnofsky ~7700 kcal/kg), à comparer au réel.
export function energyBalance(dailyIntakes: readonly number[], tdeeKcal: number): EnergyBalance {
  const deficit = dailyIntakes.reduce((acc, kcal) => acc + (tdeeKcal - kcal), 0);
  return {
    cumulativeDeficitKcal: round(deficit),
    predictedLossKg: round(deficit / KCAL_PER_KG_FAT, 2),
  };
}

export interface DeficitBreakdown {
  date: string;
  foodDeficitKcal: number; // cumul (TDEE − apport)
  sportDeficitKcal: number; // cumul des calories brûlées (exercice ponctuel, EN PLUS du TDEE)
  totalDeficitKcal: number; // cumul (food + sport)
}

/**
 * Déficit cumulé décomposé en 3 séries : alimentaire (TDEE − apport), sport
 * (calories brûlées, additionnelles au TDEE qui inclut déjà l'activité de base),
 * et total. Les dates apport/sport sont unifiées avant cumul pour éviter tout décalage.
 */
export function cumulativeDeficitBreakdownSeries(
  dailyIntakes: readonly { date: string; kcal: number }[],
  dailyBurned: readonly { date: string; kcal: number }[],
  tdeeKcal: number,
): DeficitBreakdown[] {
  const byDate = new Map<string, { intake: number; burned: number }>();
  for (const d of dailyIntakes) {
    const e = byDate.get(d.date) ?? { intake: 0, burned: 0 };
    e.intake += d.kcal;
    byDate.set(d.date, e);
  }
  for (const d of dailyBurned) {
    const e = byDate.get(d.date) ?? { intake: 0, burned: 0 };
    e.burned += d.kcal;
    byDate.set(d.date, e);
  }
  const dates = [...byDate.keys()].sort((a, b) => a.localeCompare(b));

  let food = 0;
  let sport = 0;
  return dates.map((date) => {
    const { intake, burned } = byDate.get(date)!;
    // Un jour sans apport saisi (intake=0) ne crée pas de déficit alimentaire fictif.
    if (intake > 0) food += tdeeKcal - intake;
    sport += burned;
    return {
      date,
      foodDeficitKcal: round(food),
      sportDeficitKcal: round(sport),
      totalDeficitKcal: round(food + sport),
    };
  });
}
